import React, { useMemo } from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';

export type NumericFieldProps = Omit<TextFieldProps, 'type'> & {
  /** Block minus key to prevent negative values */
  blockNegative?: boolean;
  /** Allow decimal (dot) input. Default: false */
  allowDecimal?: boolean;
  /** Minimum allowed value (clamped on blur) */
  min?: number;
  /** Maximum allowed value (clamped on blur) */
  max?: number;
};

const ALLOWED_KEYS = new Set([
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
  'Home', 'End',
]);

/**
 * Number input field (type="text" + inputMode).
 * Full control over cursor position and selection.
 * Passes all standard TextField props through.
 */
const NumericField = React.forwardRef<HTMLDivElement, NumericFieldProps>(
  ({ blockNegative = false, allowDecimal = false, min, max, onKeyDown, onChange, onBlur, sx, inputProps, ...rest }, ref) => {

    const mergedSx = useMemo(
      () => (Array.isArray(sx) ? sx : sx ? [sx] : []),
      [sx],
    );

    const computedInputMode = blockNegative
      ? (allowDecimal ? 'decimal' as const : 'numeric' as const)
      : undefined;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Allow navigation & control keys
      if (ALLOWED_KEYS.has(e.key) || e.ctrlKey || e.metaKey) {
        onKeyDown?.(e);
        return;
      }

      const input = e.target as HTMLInputElement;

      // Minus key
      if (e.key === '-') {
        if (blockNegative || input.selectionStart !== 0) {
          e.preventDefault();
          onKeyDown?.(e);
          return;
        }
        // Allow if no minus, or if selection covers the existing minus
        if (input.value.includes('-')) {
          const selEnd = input.selectionEnd ?? 0;
          if (selEnd === 0) {
            e.preventDefault();
          }
        }
        onKeyDown?.(e);
        return;
      }

      // Decimal point
      if (e.key === '.' || e.key === ',') {
        if (!allowDecimal || input.value.includes('.')) {
          e.preventDefault();
        }
        onKeyDown?.(e);
        return;
      }

      // Only digits allowed
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }

      onKeyDown?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let val = e.target.value;

      // Sanitize (handles paste)
      const hasMinus = val.charAt(0) === '-' && !blockNegative;
      val = val.replace(/[^0-9.]/g, '');

      // Remove dots if decimal not allowed
      if (!allowDecimal) {
        val = val.replace(/\./g, '');
      } else {
        // Keep only first dot
        const dotIdx = val.indexOf('.');
        if (dotIdx !== -1) {
          val = val.slice(0, dotIdx + 1) + val.slice(dotIdx + 1).replace(/\./g, '');
        }
      }

      // Re-add minus
      if (hasMinus) val = '-' + val;

      // Strip leading zeros (keep "0", "0.", "-0", "-0.")
      if (val.length > 1 && val.startsWith('0') && val[1] !== '.') {
        val = val.replace(/^0+/, '') || '0';
      }
      if (val.length > 2 && val.startsWith('-0') && val[2] !== '.') {
        val = '-' + (val.slice(1).replace(/^0+/, '') || '0');
      }

      e.target.value = val;
      onChange?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const val = e.target.value;
      if (val === '' || val === '-') {
        if (min !== undefined) {
          e.target.value = String(min);
          onChange?.(e as any);
        } else {
          e.target.value = '';
          onChange?.(e as any);
        }
      } else {
        const num = parseFloat(val);
        if (!isNaN(num)) {
          let clamped = num;
          if (min !== undefined && clamped < min) clamped = min;
          if (max !== undefined && clamped > max) clamped = max;
          const cleaned = String(clamped);
          if (cleaned !== val) {
            e.target.value = cleaned;
            onChange?.(e as any);
          }
        }
      }
      onBlur?.(e);
    };

    return (
      <TextField
        ref={ref}
        type="text"
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={mergedSx}
        inputProps={{
          inputMode: computedInputMode,
          ...inputProps,
        }}
        {...rest}
      />
    );
  }
);

NumericField.displayName = 'NumericField';

export default NumericField;
